/**
 * The order-status ladder: `nextAction` verb -> fulfillment status, and the pickup progression.
 *
 * A leaf of `myOrderHelpers` — imports nothing but constants and the `myOrders/buckets` table
 * (itself constants-only), so it stays loadable outside a booted app (see the note in `shared.ts`, which re-exports all three). Consumers import the
 * folder, never this file.
 *
 * ⚠️ CONVERGED 2026-09-08 onto WEB's bodies. `getPickupOrderNextStatus` previously differed per
 * platform, and the difference was DEMAND-DRIVEN, not permanent: mobile's ladder was simply
 * shorter, missing the "Prepared" rung and the "Dispatched" -> "Delivered" step. Nothing about a
 * phone prevents either. Web is the source of truth for behaviour, so mobile adopts web's — a
 * user-visible GAIN at its three call sites (`commonComponents/MyOrderCard`,
 * `components/MyOrderCart`, `components/MyOrderCart/myOrderCartHeader`), where a pickup order at
 * "Prepared" or "Dispatched" showed a blank next-status and now shows a real one.
 */
import {
  FULFILLMENT_STATUS_TYPES,
  NEXT_ACTION_TYPES,
  ONLINE,
  PICKUP,
  SALE_STATUS_TYPES,
} from "../../utils/constants/shared";

/** Web's `nextAction.action` verb -> the fulfillment status it moves the order into. */
export function getFulfillmentMapping(status: string = "") {
  switch (status) {
    case "Accept":
      return "Accepted";
    case "Process":
      return "Processed";
    case "Dispatch":
      return "Dispatched";
    case "Delivered":
      return "Delivered";
    case "Deliver":
      return "Delivered";
  }
}

/**
 * The pickup progression, web's version — now both platforms'.
 *
 * ⚠️ Returns `""` for an unrecognised status, deliberately distinct from the `undefined` its
 * sibling `getFulfillmentMapping` returns. Callers distinguish them; do not merge the pair.
 */
export function getPickupOrderNextStatus(status: string = "") {
  if (status === "Created" || status === "Accepted" || status === "Prepared") return "Processed";
  else if (status === "Processed") return "Dispatched";
  else if (status === "Dispatched") return "Delivered";
  return "";
}

/**
 * The status an order should DISPLAY as next.
 *
 * Was `.desktop`-only for one reason: it called the then-divergent `getPickupOrderNextStatus`.
 * That sibling is converged, so this follows it into `shared.ts` and mobile gains it.
 *
 * ⚠️ Self-delivery short-circuits: a self-delivered order at `Processed` displays as
 * `Delivered`, because there is no third-party rider leg to wait on. Mobile passed no
 * `isSelfDelivery` at all before this, so the argument defaults `false` there and the branch is
 * inert until its card supplies the flag — additive, nothing moves until it does.
 */
export function getOrderFulfillmentStatus(orderDataObj: any, isSelfDelivery: boolean = false) {
  if (isSelfDelivery && orderDataObj.fulfillmentStatus === FULFILLMENT_STATUS_TYPES.PROCESSED) {
    return FULFILLMENT_STATUS_TYPES.DELIVERED;
  }

  if (orderDataObj.deliveryInfo?.mode !== PICKUP) {
    return getFulfillmentMapping(getResolvedNextAction(orderDataObj, isSelfDelivery));
  }

  return getPickupOrderNextStatus(orderDataObj.fulfillmentStatus);
}

/*
 * ── DP-8153 (release_4.0.0, ported at the merge of 2026-10-05) ──────────────────────────────────
 * Web's `helper/myOrderHelpers` gained the five symbols below, so they live here, its mirror,
 * and the web file re-exports them. Bodies verbatim; only the imports changed.
 */

/**
 * Mirrors the net effect of the legacy POS's `Pos.SaleHelper.getValidActions` (SaleHelper.js):
 * it force-overwrites `sourceActions.delivered = true` whenever a sale carries a `sourceActions`
 * object at all, letting aggregator/online orders be marked Delivered straight past Dispatched
 * even in Pickup mode, whatever `sourceActions.delivered`'s raw backend value.
 *
 * `sourceActions` (`sale.sourceInfo.actions`) is only ever populated for online/aggregator
 * orders, so its presence alone is the test. The list endpoint flattens it to a top-level
 * `sourceActions`; the single-sale `sale/{id}/open/view` detail endpoint keeps it nested at
 * `sourceInfo.actions`, so both shapes are checked here rather than at every call site.
 */
export function isSourceDeliveredForced(orderDataObj: any): boolean {
  return !!(orderDataObj?.sourceActions || orderDataObj?.sourceInfo?.actions);
}

// Seconds the CTA gives an operator when the backend sends no `nextAction.actionTime`:
// the accept window, and the generic (process/dispatch) window. Same numbers the legacy POS used.
export const DEFAULT_ACCEPT_IN_SECONDS = 180;
export const DEFAULT_NEXT_ACTION_DUE_SECONDS = 600;

const PREPARED = "Prepared";

export function isMarkedSoldOut(orderDataObj: any) {
  return !!orderDataObj?.eventLog?.some((event: any) => event?.status === "SoldOut");
}

/**
 * The action the order CTA should perform, derived from the sale's OWN state, for when the
 * backend sent no `nextAction` at all. The legacy POS never depended on `nextAction` to decide
 * what the button does: it derived the action from `fulfillmentStatus` + `deliveryInfo` and used
 * `nextAction` only to refine the due time (and to switch an advance order to "Remind").
 *
 * Returns "" when the order has nothing to do (cancelled/voided, sold out, returns, delivered,
 * or a plain in-store order that is merely "In Process").
 */
export function getFallbackNextAction(orderDataObj: any, isSelfDelivery: boolean = false): string {
  const saleStatus = orderDataObj?.saleStatus,
    fulfillmentStatus = orderDataObj?.fulfillmentStatus,
    deliveryInfo = orderDataObj?.deliveryInfo,
    isOnline = orderDataObj?.channel?.toLowerCase() === ONLINE;

  if (
    saleStatus === SALE_STATUS_TYPES.CANCELLED ||
    saleStatus === SALE_STATUS_TYPES.VOIDED ||
    isMarkedSoldOut(orderDataObj) ||
    [
      FULFILLMENT_STATUS_TYPES.CANCELLED,
      FULFILLMENT_STATUS_TYPES.DELIVERED,
      FULFILLMENT_STATUS_TYPES.RETURN_INITIATED,
      FULFILLMENT_STATUS_TYPES.RETURN_COMPLETED,
      FULFILLMENT_STATUS_TYPES.RETURN_FAILED,
    ].includes(fulfillmentStatus)
  ) {
    return "";
  }

  // ⚠️ `saleStatus` plays no part below (beyond the guard above): a prepaid online order arrives
  // already `Closed` with its fulfillment leg untouched, so only `fulfillmentStatus` says what
  // is left to do.
  switch (fulfillmentStatus) {
    case FULFILLMENT_STATUS_TYPES.CREATED:
      // Only an online order has something to do here; a Pickup order never reaches this helper.
      return isOnline ? NEXT_ACTION_TYPES.ACCEPT : "";
    case FULFILLMENT_STATUS_TYPES.CONFIRMED:
      return isOnline || deliveryInfo ? NEXT_ACTION_TYPES.ACCEPT : "";
    case FULFILLMENT_STATUS_TYPES.ACCEPTED:
    case PREPARED:
      return isOnline || deliveryInfo ? NEXT_ACTION_TYPES.PROCESS : "";
    case FULFILLMENT_STATUS_TYPES.PROCESSED:
      if (!deliveryInfo?.mode) return "";
      return isSelfDelivery ? NEXT_ACTION_TYPES.DELIVER : NEXT_ACTION_TYPES.DISPATCH;
    default:
      // Dispatched keeps the callers' existing "mark delivered" path; nothing else is actionable.
      return "";
  }
}

/** The action to act on: what the backend sent, else the one derived from the sale's own state. */
export function getResolvedNextAction(orderDataObj: any, isSelfDelivery: boolean = false): string {
  return orderDataObj?.nextAction?.action || getFallbackNextAction(orderDataObj, isSelfDelivery);
}

/**
 * Due time for the CTA countdown when the backend sent no `nextAction.actionTime`: the legacy POS
 * showed a "by <time>" on every actionable button, computed off the last event date.
 */
export function getFallbackActionDueTime(
  orderDataObj: any,
  lastEventDate: Date,
  isSelfDelivery: boolean = false,
) {
  const action = getFallbackNextAction(orderDataObj, isSelfDelivery);
  if (!action || !lastEventDate || isNaN(lastEventDate.getTime())) return "";
  const dueSeconds =
    action === NEXT_ACTION_TYPES.ACCEPT
      ? orderDataObj?.acceptInSeconds || DEFAULT_ACCEPT_IN_SECONDS
      : DEFAULT_NEXT_ACTION_DUE_SECONDS;
  return new Date(lastEventDate.getTime() + dueSeconds * 1000).toISOString();
}


/**
 * `getSendData` moved to `myOrders/buckets` on 2026-09-15 — its two switches were the tab table
 * written out by hand. Re-exported so `helpers/myOrderHelpers` and its test keep their import.
 */
export { getSendData } from "../../myOrders/buckets";

/** True when a closed sale still has money outstanding. Identical in both apps. */
export const isDue = (orderData: any) => {
  let balance =
    orderData?.paymentInfo?.balanceAmount || orderData?.paymentInfo?.balanceAmount === 0
      ? orderData?.paymentInfo?.balanceAmount
      : orderData?.balanceAmount;
  return orderData?.saleStatus === "Closed" && balance !== 0;
};

/**
 * Every item's verified quantity equals its quantity — the detail's ALL VERIFIED chip. Web's
 * `helper/myOrderHelpers.isAllItemsVerified`, verbatim; mobile has no such function (and no
 * chip), so the phone gains it with the shared detail.
 */
export const isAllItemsVerified = (orderData: any) => {
  if (!orderData?.saleItems?.length) {
    return false;
  }
  return orderData.saleItems.every((item: any) => {
    const quantity = item?.itemQuantity ?? item?.quantity ?? 0;
    const verifiedQuantity = item?.verifiedQuantity ?? 0;
    return quantity > 0 && verifiedQuantity === quantity;
  });
};

/** True when nothing is outstanding. Identical in both apps modulo prettier wrapping. */
export const isPaid = (orderData: any) => {
  return orderData?.balanceAmount === 0 || orderData?.paymentInfo?.balanceAmount === 0;
};
